<template>
  <v-stepper-content step="6">
    <v-snackbar
      top
      color="error"
      v-model="snackbar"
      :timeout="6000"
      style="z-index: 9999"
    >
      {{ $t("payment.error") }}
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-card class="mx-1 mb-12">
      <v-card-title>{{ $t("form.labels.payment") }}</v-card-title>
      <v-card-text>
        <v-tabs v-model="tab">
          <v-tab>
            <v-icon class="mr-2">mdi-credit-card</v-icon>
            {{ $t("form.labels.creditcard") }}
          </v-tab>
          <v-tab>
            <v-icon class="mr-2">mdi-bank</v-icon>
            {{ $t("form.labels.sofort") }}
          </v-tab>
          <v-tab>
            <v-icon class="mr-2">mdi-account-cash</v-icon>
            {{ $t("form.labels.banktransfer") }}
          </v-tab>

          <v-tab-item>
            <v-card flat class="mt-4">
              <v-form>
                <v-stripe-card
                  outlined
                  :api-key="apiKey"
                  :zip="registration.invoice.zip"
                  ref="stripeCard"
                  v-model="token"
                  :label="$t('form.labels.creditcard')"
                ></v-stripe-card>
              </v-form>
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card flat class="mt-4"> </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card flat class="mt-4">
              <v-card-title>{{
                $t("form.bankTransfer.headline")
              }}</v-card-title>
              <v-card-text>
                <span class="subtitle-2">{{
                  $t("form.bankTransfer.text")
                }}</span>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs>

        <v-checkbox outlined v-model="agb">
          <template v-slot:label>
            <span v-html="$t('form.fields.agb') + ' *'"></span>
          </template>
        </v-checkbox>
        <v-checkbox
          outlined
          v-model="registrationFormAdditionalCheck"
          v-if="showRegistrationFormAdditionalText"
        >
          <template #label>
            <span v-html="registrationFormAdditionalText"></span>
          </template>
        </v-checkbox>
      </v-card-text>
    </v-card>
    <v-layout>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        class="mr-4"
        @click="submit"
        :disabled="!agb || loading || !registrationFormAdditionalCheck"
      >
        {{ $t("form.btn.pay") }}
      </v-btn>
      <v-btn text @click="$emit('onBack')">{{ $t("form.btn.back") }}</v-btn>
    </v-layout>
  </v-stepper-content>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
export default {
  data() {
    return {
      token: null,
      tab: null,
      agb: null,
      registrationFormAdditionalCheck:
        !window.showRegistrationFormAdditionalText ||
        window.showRegistrationFormAdditionalText === "false",
      snackbar: null,
    };
  },
  props: ["pageType"],

  mounted() {
    const queryParams = new URLSearchParams(window.location.search);
    const paymentIntentClientSecret = queryParams.get(
      "payment_intent_client_secret"
    );

    if (paymentIntentClientSecret) {
      this.toggleLoading(true);
      this.checkStripeCard(paymentIntentClientSecret);
    }
  },

  methods: {
    ...mapActions([
      "authorize",
      "authorizeBankTransfer",
      "toggleLoading",
      "charge",
    ]),
    ...mapMutations(["setPayment"]),
    async checkStripeCard(paymentIntentClientSecret) {
      if (this.$refs.stripeCard && this.$refs.stripeCard.stripe) {
        try {
          const stripe = this.$refs.stripeCard.stripe;
          const { paymentIntent } = await stripe.retrievePaymentIntent(
            paymentIntentClientSecret
          );
          if (paymentIntent.status === "succeeded") {
            const url = new URL(window.location.href);
            url.search = "";
            window.history.pushState({}, "", url.href);
            this.setPayment({
              id: paymentIntent.id,
              method: "klarna",
            });
            this.$emit("onContinue");
          } else {
            console.log("PaymentIntent status: ", paymentIntent.status);
            this.snackbar = true;
          }
        } catch (err) {
          console.log("error ", err);
          this.snackbar = true;
        } finally {
          this.toggleLoading(false);
        }
      } else {
        setTimeout(() => this.checkStripeCard(paymentIntentClientSecret), 100); // Check again after 100ms
      }
    },
    submit() {
      if (this.tab === 0) {
        this.submitCardPayment();
      }
      if (this.tab === 1) {
        this.submitSofortPayment();
      }
      if (this.tab === 2) {
        this.authorizeBankTransfer();
        this.$emit("onContinue");
      }
    },

    async submitCardPayment() {
      this.toggleLoading(true);
      const paymentIntent = await this.authorize();
      const stripe = this.$refs.stripeCard.stripe;
      try {
        const res = await stripe.confirmCardPayment(
          paymentIntent.result.client_secret,
          {
            payment_method: {
              card: this.$refs.stripeCard.card,
              billing_details: {
                name:
                  this.registration.invoice.firstname +
                  " " +
                  this.registration.invoice.lastname,
                email: this.registration.invoice.email,
              },
            },
            save_payment_method: false,
          }
        );

        if (res.paymentIntent.status === "succeeded") {
          this.setPayment({
            id: res.paymentIntent.id,
            method: "card",
          });
          this.$emit("onContinue");
        }
      } catch (err) {
        this.snackbar = true;
      } finally {
        this.toggleLoading(false);
      }
    },

    async submitSofortPayment() {
      this.toggleLoading(true);
      const paymentIntent = await this.authorize();
      const stripe = this.$refs.stripeCard.stripe;
      try {
        // remove all previous query parameters
        const url = new URL(window.location.href);
        url.search = "";
        window.history.pushState({}, "", url.href);
        await stripe.confirmKlarnaPayment(paymentIntent.result.client_secret, {
          payment_method: {
            billing_details: {
              name:
                this.registration.invoice.firstname +
                " " +
                this.registration.invoice.lastname,
              email: this.registration.invoice.email,
              address: {
                city: this.registration.invoice.city,
                country: this.registration.invoice.country,
                postal_code: this.registration.invoice.zip,
              },
            },
          },
          return_url: window.location.href,
          save_payment_method: false,
        });
      } catch (err) {
        this.snackbar = true;
      } finally {
        this.toggleLoading(false);
      }
    },
  },

  computed: {
    ...mapGetters(["registration", "cartSum", "loading"]),
    apiKey() {
      return (
        window.stripePublicKey ||
        process.env["VUE_APP_STRIPE_PUBLIC_KEY_" + this.pageType]
      );
    },

    showRegistrationFormAdditionalText() {
      return (
        window.showRegistrationFormAdditionalText &&
        window.showRegistrationFormAdditionalText !== "false" &&
        this.registrationFormAdditionalText.length
      );
    },
    registrationFormAdditionalText() {
      const div = document.createElement("div");
      div.innerHTML = window.registrationFormAdditionalText;
      return div.firstChild ? div.firstChild.innerHTML : "";
    },
  },
};
</script>

<style></style>
